import React from 'react';
import PropTypes from 'prop-types';
import { StatusCodes } from 'http-status-codes';
import { Formik } from 'formik';
import * as yup from 'yup';
import SupplierProductsApi from '../../../../services/api/SupplierProductsApi';
import TextInputComponent from '../../../../components/common/forms/TextInput/TextInputComponent';
import FormRow from '../../../../components/common/forms/Row/FormRow';
import CheckboxInputComponent from '../../../../components/common/forms/CheckboxInput/CheckboxInputComponent';
import { TEMPERATURE_CONTROL_TYPES } from '../../../../utils/constants';
import AutocompleteStaticComponent from '../../../../components/common/forms/Autcomplete/AutocompleteStaticComponent';
import { constantsToAutocomplete } from '../../../../utils/misc';
import styled from 'styled-components';
import { Colors } from '~/src/scss/common/style';

const validationSchema = yup.object().shape({
  sku: yup.string().label('SKU'),
  externalName: yup.string().label('Supplier Name'),
  priceOfCuForDus: yup.number(),
  priceOfDuForDus: yup.number(),
  priceOfCuForLayers: yup.number(),
  priceOfDuForLayers: yup.number(),
  priceOfCuForPallets: yup.number(),
  priceOfDuForPallets: yup.number(),
  moqPallet: yup.bool(),
  moqLayer: yup.bool(),
  defaultTemperatureControl: yup.string().required(),
  ea: yup.string().label('EA'),
  adsz: yup.string().label('EA'),
  DutyEUfromUSA: yup.string().label('EA'),
  MeursingCode: yup.number().integer(),
  extraDutyUSA: yup.number(),
});

interface SupplierProductDetailsProps {
  supplierProduct: any;
}

export default function SupplierProductDetails({ supplierProduct }: SupplierProductDetailsProps): JSX.Element {
  const updateProduct = async (values, setFieldValue) => {
    const res = await SupplierProductsApi.update(supplierProduct.id, values);
    if (setFieldValue) {
      setFieldValue('priceOfCuForDus', res.data.priceOfCuForDus);
      setFieldValue('priceOfDuForDus', res.data.priceOfDuForDus);
      setFieldValue('priceOfCuForLayers', res.data.priceOfCuForLayers);
      setFieldValue('priceOfDuForLayers', res.data.priceOfDuForLayers);
      setFieldValue('priceOfCuForPallets', res.data.priceOfCuForPallets);
      setFieldValue('priceOfDuForPallets', res.data.priceOfDuForPallets);
    }
  };

  return (
    <div style={{ background: ' #fff' }}>
      <Title>Supplier Data</Title>
      <Formik
        initialValues={{
          sku: supplierProduct.sku || '',
          externalName: supplierProduct.externalName || '',
          priceOfCuForDus: supplierProduct.priceOfCuForDus || '',
          priceOfDuForDus: supplierProduct.priceOfDuForDus || '',
          priceOfCuForLayers: supplierProduct.priceOfCuForLayers || '',
          priceOfDuForLayers: supplierProduct.priceOfDuForLayers || '',
          priceOfCuForPallets: supplierProduct.priceOfCuForPallets || '',
          priceOfDuForPallets: supplierProduct.priceOfDuForPallets || '',
          moqPallet: supplierProduct.moqPallet || false,
          moqLayer: supplierProduct.moqLayer || false,
          defaultTemperatureControl:
            supplierProduct.defaultTemperatureControl || TEMPERATURE_CONTROL_TYPES.NON_REEFER,
          hScodeSupplier: supplierProduct.hScodeSupplier || '',
          dutyEUfromUSA: supplierProduct.dutyEUfromUSA || '',
          meursingCode: supplierProduct.meursingCode || '',
          ea: supplierProduct.ea || '',
          adsz: supplierProduct.adsz || '',
          extraDutyUSA: supplierProduct.extraDutyUSA !== null ? supplierProduct.extraDutyUSA : '',
        }}
        validationSchema={validationSchema}
      >
        {({ values, handleChange, isSubmitting, setFieldValue, errors }) => (
          <form autoComplete="off" style={{ margin: '5px' }}>
            <FormRow>
              <TextInputComponent
                label="External name"
                name="externalName"
                value={values.externalName}
                onBlur={(e) => {
                  updateProduct(values);
                }}
                onChange={handleChange}
                cols={6}
              />
              <TextInputComponent
                label="Sku"
                name="sku"
                value={values.sku}
                onBlur={(e) => {
                  updateProduct(values);
                }}
                onChange={handleChange}
                cols={6}
              />
            </FormRow>
            <FormRow>
              <CheckboxInputComponent
                label="Pallet minimum order"
                id="moqPallet"
                name="moqPallet"
                checked={values.moqPallet}
                onChange={(e, v) => {
                  setFieldValue('moqPallet', e.target.checked);
                  updateProduct({ ...values, moqPallet: e.target.checked });
                }}
                cols={3}
              />
              <CheckboxInputComponent
                label="Layer minimum order"
                id="moqLayer"
                name="moqLayer"
                checked={values.moqLayer}
                onChange={(e, v) => {
                  setFieldValue('moqLayer', e.target.checked);
                  updateProduct({ ...values, moqLayer: e.target.checked });
                }}
                cols={3}
              />

              <AutocompleteStaticComponent
                options={constantsToAutocomplete(TEMPERATURE_CONTROL_TYPES)}
                name="defaultTemperatureControl"
                initialValue={values.defaultTemperatureControl}
                onChange={(e, v) => {
                  setFieldValue('defaultTemperatureControl', v);
                  updateProduct({ ...values, defaultTemperatureControl: v });
                }}
                getOptionLabel={(option) => `${option.label}`}
                cols={6}
                errorMessage={errors.defaultTemperatureControl}
              />
            </FormRow>
            <FormRow>
              <TextInputComponent
                label="Price of Cu for Dus"
                name="priceOfCuForDus"
                value={values.priceOfCuForDus}
                onBlur={(e) => {
                  values.priceOfDuForDus = null;
                  updateProduct(values, setFieldValue);
                }}
                onChange={handleChange}
                type="number"
                cols={4}
              />
              <TextInputComponent
                label="Price of Cu for layers"
                name="priceOfCuForLayers"
                value={values.priceOfCuForLayers}
                onBlur={(e) => {
                  values.priceOfDuForLayers = null;
                  updateProduct(values, setFieldValue);
                }}
                onChange={handleChange}
                type="number"
                cols={4}
              />
              <TextInputComponent
                label="Price of Cu for pallets"
                name="priceOfCuForPallets"
                value={values.priceOfCuForPallets}
                onBlur={(e) => {
                  values.priceOfDuForPallets = null;
                  updateProduct(values, setFieldValue);
                }}
                onChange={handleChange}
                type="number"
                cols={4}
              />
            </FormRow>
            <FormRow>
              <TextInputComponent
                label="Price of Cu for Dus"
                name="priceOfDuForDus"
                value={values.priceOfDuForDus}
                onBlur={(e) => {
                  values.priceOfCuForDus = null;
                  updateProduct(values, setFieldValue);
                }}
                onChange={handleChange}
                type="number"
                cols={4}
              />
              <TextInputComponent
                label="Price of Cu for layers"
                name="priceOfDuForLayers"
                value={values.priceOfDuForLayers}
                onBlur={(e) => {
                  values.priceOfCuForLayers = null;
                  updateProduct(values, setFieldValue);
                }}
                onChange={handleChange}
                type="number"
                cols={4}
              />
              <TextInputComponent
                label="Price of Cu for pallets"
                name="priceOfDuForPallets"
                value={values.priceOfDuForPallets}
                onBlur={(e) => {
                  values.priceOfCuForPallets = null;
                  updateProduct(values, setFieldValue);
                }}
                onChange={handleChange}
                type="number"
                cols={4}
              />
            </FormRow>
            <FormRow>
              <div style={{ width: '20%', padding: '0 5px' }}>
                <TextInputComponent
                  label="Ea"
                  name="ea"
                  value={values.ea}
                  onBlur={(e) => {
                    updateProduct(values);
                  }}
                  onChange={handleChange}
                  cols={12}
                />
              </div>
              <div style={{ width: '20%', padding: '0 5px' }}>
                <TextInputComponent
                  label="Adsz"
                  name="adsz"
                  value={values.adsz}
                  onBlur={(e) => {
                    updateProduct(values);
                  }}
                  onChange={handleChange}
                  cols={12}
                />
              </div>
              <div style={{ width: '20%', padding: '0 5px' }}>
                <TextInputComponent
                  label="Duty EU from USA"
                  name="dutyEUfromUSA"
                  value={values.dutyEUfromUSA}
                  onBlur={(e) => {
                    updateProduct(values);
                  }}
                  onChange={handleChange}
                  cols={12}
                />
              </div>
              <div style={{ width: '20%', padding: '0 5px' }}>
                <TextInputComponent
                  label="Meursing code"
                  name="meursingCode"
                  value={values.meursingCode}
                  onBlur={(e) => {
                    updateProduct(values);
                  }}
                  onChange={handleChange}
                  type="number"
                  cols={12}
                />
              </div>
              <div style={{ width: '20%', padding: '0 5px' }}>
                <TextInputComponent
                  label="Extra Duty USA"
                  name="extraDutyUSA"
                  value={values.extraDutyUSA}
                  onBlur={(e) => {
                    updateProduct(values);
                  }}
                  onChange={handleChange}
                  type="number"
                  cols={12}
                />
              </div>
            </FormRow>
          </form>
        )}
      </Formik>
    </div>
  );
}

const Title = styled.h2`
  font-weight: bold;
  font-size: 20px;
  color: #343434;
  color: ${Colors.darkBrown};
  text-align: center;
  width: 100%;
  padding: 60px 0 40px 0;
`;
